import { assertNever } from "../../../../types/internal/utility";
import { SALE_TRANSACTION_FINISHED } from "../../../_Bootstrap/GlobalConstants";
import type { LocalDatabaseProduct } from "../../../_Generated/Sync.gen";
import NotFoundException from "../../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import { union } from "../../../Utilities/Generic/Arrays";
import Cloner from "../../../Utilities/Generic/Cloner";
import { SearchCommunicator } from "../../../Workers/Search/SearchCommunicator";
import { SearchIndexes } from "../../../Workers/Search/SearchEvents";
import type { BaseProductRepository } from "../../_Bases/BaseProductRepository";
import type {
    ProductClassificationUnion,
    ProductRepositorySearchOptions,
    ProductSearchResultType,
    ProductStatusUnion,
    ProductTypeUnion,
} from "../../_Bases/BaseProductRepository";
import type { InternalIndexedDBDatabase } from "../../DataStores/Database";
import BaseIndexedDBRepository from "../../Implementations/BaseIndexedDBRepository";
import { getProductRepositorySearchOptions } from "./ProductRepositoryShared";

export default class IndexedDBProductRepository extends BaseIndexedDBRepository<
    LocalDatabaseProduct
> implements BaseProductRepository {
    public source: InternalIndexedDBDatabase["products"];
    protected readonly dataTypeName = "Product";
    protected readonly broadcastChannel = new BroadcastChannel("productRepository");

    protected syncing: boolean;

    protected productCache: Record<string, LocalDatabaseProduct>;

    constructor(source: InternalIndexedDBDatabase) {
        super();

        this.source = source.products;
        this.syncing = false;

        this.productCache = {};

        // Listen for transaction end events
        if(typeof document !== "undefined") {
            document.addEventListener(SALE_TRANSACTION_FINISHED, this.resetProductCache);
            document.addEventListener("synchronisationFinished", (event: CustomEvent) => {
                if(!event.detail.updated?.products) {
                    return;
                }

                this.resetProductCache();
            });
        }

        this.broadcastChannel.addEventListener("message", event => {
            if(typeof event.data !== "object" || event.data === null) {
                return;
            }

            if(typeof event.data.id !== "string") {
                return;
            }

            if(event.data.action !== "PUT") {
                return;
            }

            delete this.productCache[event.data.id];
        });
    }

    protected resetProductCache = (): void => {
        this.productCache = {};
    };

    public clearCache(): void {
        this.resetProductCache();
    }

    protected async wildcardSearch(
        value: string,
        options: ProductRepositorySearchOptions
    ): Promise<Array<ProductSearchResultType>> {
        value = value.trim();

        const results: Array<string> = [];

        if(options.name) {
            const searchResults = await SearchCommunicator.search(SearchIndexes.PRODUCT_NAME, value);

            for(let i = 0, l = searchResults.length; i < l; i++) {
                results.push(searchResults[i]);
            }
        }

        const matchingBarcode = new Set<string>();

        if(options.barcode) {
            const searchResults = await SearchCommunicator.search(SearchIndexes.PRODUCT_BARCODE, value);

            for(let i = 0, l = searchResults.length; i < l; i++) {
                results.push(searchResults[i]);
                matchingBarcode.add(searchResults[i]);
            }
        }

        if(!results.length) {
            return [];
        }

        let query = this.source
            .where("uuid")
            .anyOf(results);

        if(typeof options.filter === "function") {
            query = query.filter(options.filter);
        }

        return query
            .limit(options.limit)
            .sortBy("name")
            .then((products: Array<LocalDatabaseProduct>): Array<LocalDatabaseProduct> => {
                // No barcodes to match
                if(!matchingBarcode.size) {
                    return products;
                }

                // Add the barcode quantity to the results
                const results: Array<ProductSearchResultType> = [];

                for(const product of products) {
                    if(!matchingBarcode.has(product.uuid)) {
                        results.push(product);
                        continue;
                    }

                    let quantity = 1;

                    for(const barcode of product.barcodes) {
                        if(barcode.code !== value) {
                            continue;

                        }

                        if(typeof options.barcode === "string") {
                            if(barcode.template !== options.barcode) {
                                continue;
                            }
                        }

                        const parsed = parseFloat(barcode.quantity);

                        if(isNaN(parsed)) {
                            continue;
                        }

                        quantity = parsed;
                        break;
                    }

                    results.push({
                        ...product,
                        kind         : "product",
                        searchBarcode: value,
                        quantity,
                    });
                }

                return results;
            });
    }

    protected async exactSearch(
        value: string,
        options: ProductRepositorySearchOptions
    ): Promise<Array<ProductSearchResultType>> {
        const promises: Array<Promise<Array<ProductSearchResultType>>> = [];
        let values = [{
            value,
            quantity: 1,
            kind    : "product",
        }];

        if(options.quantityCheck) {
            let quantity = options.quantityCount;

            while(quantity > 0) {
                const q = parseFloat(value.slice(0, quantity));

                if(isNaN(q)) {
                    quantity--;
                    continue;
                }

                values.push({
                    value   : value.substr(quantity),
                    kind    : "product",
                    quantity: q,
                });

                quantity--;
            }

            const newValues = [];

            for(let i = 0, l = values.length; i < l; i++) {
                if(values[i].value === "") continue;

                newValues.push(values[i]);
            }

            values = newValues;
        }

        if(options.name) {
            for(let i = 0, l = values.length; i < l; i++) {
                const quantity = values[i].quantity;

                let query = this.source
                    .where("name")
                    .anyOfIgnoreCase(values[i].value);

                if(typeof options.filter === "function") {
                    query = query.filter(options.filter);
                }

                promises.push(
                    query
                        .toArray()
                        .then((results: Array<ProductSearchResultType>): Array<ProductSearchResultType> => {
                            for(let i = 0, l = results.length; i < l; i++) {
                                results[i].quantity = quantity;
                                results[i].kind = "product";
                            }

                            return results;
                        })
                );
            }
        }

        if(options.barcode || options.barcode === "") {
            for(let i = 0, l = values.length; i < l; i++) {
                const quantity = values[i].quantity;
                const value = values[i].value;

                let query = this.source
                    .where("code_index")
                    .anyOfIgnoreCase(value);

                if(typeof options.filter === "function") {
                    query = query.filter(options.filter);
                }

                promises.push(
                    query
                        .toArray()
                        .then((results: Array<LocalDatabaseProduct>): Array<ProductSearchResultType> => {
                            // Find the matching barcodes
                            const res: Array<ProductSearchResultType> = [];

                            for(let i = 0, l = results.length; i < l; i++) {
                                for(let j = 0, n = results[i].barcodes.length; j < n; j++) {
                                    if(results[i].barcodes[j].code === value) {
                                        if(typeof options.barcode === "string") {
                                            if(results[i].barcodes[j].template !== options.barcode) {
                                                continue;
                                            }
                                        }

                                        let parsed = parseFloat(results[i].barcodes[j].quantity);

                                        if(isNaN(parsed)) {
                                            parsed = 1;
                                        }

                                        res.push({
                                            ...results[i],
                                            quantity     : quantity * parsed,
                                            kind         : "product",
                                            searchBarcode: value,
                                        });
                                    }
                                }
                            }

                            return res;
                        })
                );
            }
        }

        return Promise.all(promises).then((resolved) => {
            let results = resolved[0];

            if(resolved.length > 1) {
                for(let i = 1, l = resolved.length; i < l; i++) {
                    results = union(results, resolved[i]);
                }
            }

            return results;
        });
    }

    public search(
        value: string,
        options?: Partial<ProductRepositorySearchOptions>
    ): Promise<Array<ProductSearchResultType>> {
        let search;

        const opts = getProductRepositorySearchOptions(options);

        if(opts.exactMatch) {
            search = this.exactSearch(value, opts);
        } else {
            search = this.wildcardSearch(value, opts);
        }

        return search.then(results => {
            if(opts.sort) {
                results.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                });
            }

            return results;
        });
    }

    public async get(uuid: string): Promise<LocalDatabaseProduct> {
        if(typeof this.productCache[uuid] !== "undefined") {
            return Cloner.Clone(this.productCache[uuid]);
        }

        return this.source
            .get(uuid)
            .then((product) => {
                if(!product) {
                    throw new NotFoundException(`Product Not Found (${uuid})`);
                }

                this.productCache[uuid] = product;

                return product;
            });
    }

    public getBulk(ids: Array<string>): Promise<Array<LocalDatabaseProduct>> {
        return this.source.where("uuid").anyOf(ids).toArray();
    }

    public getByMDBId(mdbId: number): Promise<Array<LocalDatabaseProduct>> {
        return this.source.where("mdb_id").equals(mdbId).toArray();
    }

    public getFromClassification(type: ProductClassificationUnion, uuid: string): Promise<Array<LocalDatabaseProduct>> {
        let key = "";

        switch(type) {
            case "category":
                key = "category_id";
                break;
            case "brand":
                key = "brand_id";
                break;
            case "family":
                key = "family_id";
                break;
            case "tag":
                key = "tags";
                break;
            case "supplier":
                key = "supplier_ids";
                break;
            case "supplier_code":
                key = "supplier_codes";
                break;
            default:
                return assertNever(type);
        }

        return this.source.where(key).equals(uuid).toArray();
    }

    public getByStatus(status: ProductStatusUnion | Array<ProductStatusUnion>): Promise<Array<LocalDatabaseProduct>> {
        const isArray = Array.isArray(status);

        if(isArray && status.length === 0) {
            return this.source.toArray();
        } else if(isArray && status.length === 1) {
            return this.source.where("status").equals(status[0]).toArray();
        } else if(isArray) {
            return this.source.where("status").anyOf(status as Array<string>).toArray();
        } else {
            return this.source.where("status").equals(status).toArray();
        }
    }

    public getByType(type: ProductTypeUnion | Array<ProductTypeUnion>): Promise<Array<LocalDatabaseProduct>> {
        if(!Array.isArray(type)) {
            type = [ type ];
        }

        return this.source.where("type").anyOf(type).toArray();
    }

    public getBySupplierCodes(supplierCodes: Array<string>): Promise<Array<LocalDatabaseProduct>> {
        return this.source.where("supplier_codes").anyOfIgnoreCase(supplierCodes).toArray();
    }

    private replaceIndexes(id: string, name?: string, barcodes?: Array<string>): void {
        SearchCommunicator.updateIndex(SearchIndexes.PRODUCT_NAME, id, name);
        SearchCommunicator.updateIndex(SearchIndexes.PRODUCT_BARCODE, id, barcodes);
    }

    private clearIndexes(): void {
        SearchCommunicator.emptyIndex(SearchIndexes.PRODUCT_NAME);
        SearchCommunicator.emptyIndex(SearchIndexes.PRODUCT_BARCODE);
    }

    public put(item: Array<LocalDatabaseProduct> | LocalDatabaseProduct): Promise<string> {
        return super.put(item)
            .then(async id => {
                if(!Array.isArray(item)) {
                    item = [ item ];
                }

                for(let i = 0, l = item.length; i < l; i++) {
                    delete this.productCache[item[i].uuid];

                    this.replaceIndexes(item[i].uuid, item[i].name, item[i].code_index);
                }

                this.broadcastChannel.postMessage({
                    id,
                    action: "PUT",
                });

                return id;
            });
    }

    public delete(item: Array<string> | string): Promise<void> {
        return super.delete(item)
            .then(() => {
                if(!Array.isArray(item)) {
                    item = [ item ];
                }

                for(let i = 0, l = item.length; i < l; i++) {
                    this.replaceIndexes(item[i]);
                }
            });
    }

    public empty(): Promise<void> {
        return super.empty()
            .then(() => {
                this.clearIndexes();
            });
    }
}
